<script setup lang="ts">
import {computed, onBeforeMount, ref, watch} from "vue";
import {QSelect} from "quasar";
import Translator from "@/utils/common/Translator.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import DateAndTimeFieldWidget from "@/components/EditView/widgets/DateAndTimeFieldWidget.vue";
import TimeFieldWidget from "@/components/EditView/widgets/TimeFieldWidget.vue";
import type {FilterDisplayParams} from "ag-grid-community";

const props = defineProps<{ params: FilterDisplayParams }>();
const dateQSelect = ref<QSelect>();
const toDateQSelect = ref<QSelect>();
let equalOption = {label: Translator.translateId('Equals'), value: 'equals'};
const selectedOption = ref<Record<string, any>>(equalOption);
const filterOptions = ref<Record<string, any>[]>([
  equalOption,
  {label: Translator.translateId('NotEqual'), value: 'notEqual'},
  {label: Translator.translateId('before'), value: 'before'},
  {label: Translator.translateId('after'), value: 'after'},
  {label: Translator.translateId('between'), value: 'between'},
  {label: Translator.translateId('blank'), value: 'blank'},
  {label: Translator.translateId('notBlank'), value: 'notBlank'},
]);
const isDateVisible = computed(() => !['blank', 'notBlank'].contains(selectedOption.value.value));
const isToDateVisible = computed(() => selectedOption.value.value === 'between');
let questionFieldPresenter = new QuestionFieldsPresenter();
let fieldType = props.params['fieldType'];
let dateQuestionField = NamaCommon.createQuestionField("date", fieldType);
let toDateQuestionField = NamaCommon.createQuestionField("toDate", fieldType);
let questionFields = [dateQuestionField, toDateQuestionField];
const date = computed(() => questionFieldPresenter.record.value["date"]);
const toDate = computed(() => questionFieldPresenter.record.value["toDate"]);
const targetComponent = fieldType && fieldType === FieldType.Time ? TimeFieldWidget : DateAndTimeFieldWidget;

onBeforeMount(async () => {
  await questionFieldPresenter.edit(questionFields, []);
  if (props.params.model) {
    const model = props.params.model;
    questionFieldPresenter.record.value['date'] = model.dateFrom;
    questionFieldPresenter.record.value['toDate'] = model.dateTo;
    selectedOption.value = filterOptions.value.find(l => l.value === model.type) || equalOption;
  }
  await NamaCommon.waitFor(100)
  dateQSelect.value?.focus();
})

watch(() => questionFieldPresenter.record, updateFilterModel, {deep: true});

function onUpdateOption(newValue: Record<string, any>) {
  selectedOption.value = newValue;
  updateFilterModel();
}

function updateFilterModel() {
  const isActive = (date.value !== null && date.value !== undefined && date.value !== '') || (selectedOption.value && ['blank', 'notBlank'].contains(selectedOption.value.value));

  if (!isActive) {
    props.params.onModelChange(null);
    return;
  }

  const model = {
    dateFrom: date.value,
    dateTo: toDate.value,
    type: selectedOption.value.value,
    filterType: "date"
  };
  props.params.onModelChange(model);
}

function reset() {
  selectedOption.value = equalOption;
  questionFieldPresenter.record.value["date"] = "";
  questionFieldPresenter.record.value["toDate"] = "";
  props.params.onModelChange(null);
}
</script>

<template>
  <div class="tw-flex tw-flex-col tw-gap-2 tw-rounded tw-p-2">
    <q-select
      outlined dense options-dense
      :model-value="selectedOption"
      @update:model-value="onUpdateOption"
      :options="filterOptions"
      class="nama-criteria-field-input nama-customize-list-filter-dense-input mobile:tw-w-full ag-custom-component-popup"
      input-class="tw-bg-transparent focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-namaFontColor-dark
              dark:tw-placeholder-gray-300 header-search-in-popup-input tw-pl-2"
      options-selected-class="tw-text-base tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-namaFontColor-dark"
      popup-content-class="!tw-max-h-[30vh] navbar-search-popup dark:tw-bg-namaBackground-deep-dark dark:tw-text-white ag-custom-component-popup"
    />
    <component :is="targetComponent" ref="dateQSelect" :presenter="questionFieldPresenter" :field="dateQuestionField"
                            :field-meta-data="dateQuestionField.metaData!"
                            is-inside-composite v-if="isDateVisible"/>
    <component :is="targetComponent" ref="toDateQSelect" :presenter="questionFieldPresenter" :field="toDateQuestionField"
                            :field-meta-data="toDateQuestionField.metaData!"
                            is-inside-composite v-if="isToDateVisible"/>
    <q-btn no-caps class="tw-bg-primary-light tw-w-fit tw-self-end tw-text-namaFontColor-dark" :label="Translator.translateId('Reset')"
           @click="reset()"/>
  </div>
</template>

<style scoped>
</style>
